import { useEffect, useId, useState, type FormEvent } from "react";
import { useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import {
  PLAN_BY_ID,
  PLANS,
  planPrice,
  YEARLY_SAVING_PERCENT,
  type BillingInterval,
  type PlanId,
} from "@metrickle/schema/plans";
import { formatMoney } from "@metrickle/schema/currency";
import { REGION_LABEL, REGIONS, type Region } from "@metrickle/schema/regions";
import { api, ApiError, type WorkspacePlan } from "../../lib/api";
import { fmtNumber } from "../../lib/format";
import { roleLabel, type AccountDetail, type LimitUse } from "../../lib/admin";
import {
  Ago,
  Badge,
  Button,
  ButtonLink,
  Callout,
  Card,
  CardHeader,
  CardList,
  CardRow,
  Checkbox,
  CopyButton,
  Field,
  Input,
  Meter,
  Modal,
  RadioCards,
  Segmented,
  SelectField,
  ToggleButtons,
  cx,
  notifyError,
  toaster,
  useConfirm,
} from "../../components/ui";
import { invalidateAdmin, toDateInput } from "./components";

// Plan and discount controls for platform admins: on an account page, and when onboarding a new
// customer. Prices and plans come from packages/schema/src/plans.ts, the same catalog the pricing
// page is built from, so what you set here always matches what the customer saw.

/** Form state: numbers stay strings while being typed. */
export interface PlanDraft {
  plan: PlanId;
  interval: BillingInterval;
  customMonthly: string;
  discountPercent: string;
  discountNote: string;
  /** yyyy-mm-dd, or "" for no end date. */
  discountEndsAt: string;
}

export const draftFrom = (p?: WorkspacePlan | null): PlanDraft => ({
  plan: p?.plan ?? "free",
  interval: p?.interval ?? "month",
  customMonthly: p?.customMonthly != null ? String(p.customMonthly) : "",
  discountPercent: p?.discountPercent ? String(p.discountPercent) : "",
  discountNote: p?.discountNote ?? "",
  discountEndsAt: toDateInput(p?.discountEndsAt),
});

/** A discount ends at the close of the chosen day, local time. */
const endOfDay = (v: string) => (v ? new Date(`${v}T23:59:59`).getTime() : null);

type Parsed = { ok: true; json: Record<string, unknown> } | { ok: false; error: string };

export function parseDraft(d: PlanDraft): Parsed {
  const pct = d.discountPercent.trim() === "" ? 0 : Number(d.discountPercent);
  if (!Number.isInteger(pct) || pct < 0 || pct > 100) return { ok: false, error: "Discount must be a whole number from 0 to 100." };
  const custom = d.customMonthly.trim() === "" ? null : Number(d.customMonthly);
  if (custom !== null && (!Number.isInteger(custom) || custom < 0)) return { ok: false, error: "Monthly price must be a whole number of pounds." };
  if (d.plan === "enterprise" && custom === null) return { ok: false, error: "Enterprise needs a monthly price." };
  const ends = endOfDay(d.discountEndsAt);
  if (pct > 0 && ends !== null && ends < Date.now()) return { ok: false, error: "The discount end date is in the past." };
  return {
    ok: true,
    json: {
      plan: d.plan,
      interval: d.interval,
      customMonthly: custom,
      discountPercent: pct,
      discountNote: pct ? d.discountNote.trim() || null : null,
      discountEndsAt: pct ? ends : null,
    },
  };
}

const PRESETS = [10, 20, 30, 50, 100];

/** Plan, billing, negotiated price and discount fields, with a live price preview. */
export function PlanFields({ value, onChange }: { value: PlanDraft; onChange: (d: PlanDraft) => void }) {
  const set = (patch: Partial<PlanDraft>) => onChange({ ...value, ...patch });
  const billingId = useId();
  const plan = PLAN_BY_ID[value.plan];
  const free = value.plan === "free";
  const pct = Number(value.discountPercent) || 0;
  const custom = value.customMonthly.trim() === "" ? null : Number(value.customMonthly) || 0;
  const price = planPrice({ plan: value.plan, interval: value.interval, discountPercent: pct, customMonthly: custom });
  const list = planPrice({ plan: value.plan, interval: value.interval, customMonthly: custom });
  const per = value.interval === "year" ? "year" : "month";

  return (
    <div className="space-y-3">
      <div className="grid gap-3 @md:grid-cols-2">
        <Field label="Plan">
          <SelectField
            className="w-full"
            aria-label="Plan"
            items={PLANS.map((p) => ({ value: p.id, label: p.monthly === null ? `${p.name} (custom)` : `${p.name} · £${p.monthly}/mo` }))}
            value={value.plan}
            onChange={(v) => set({ plan: v as PlanId })}
          />
        </Field>
        {/* Segmented is a radio group, not one input, so its visible label names it by id rather than a Field <label>. */}
        <div className="space-y-1.5">
          <span id={billingId} className="block text-[13px] font-medium text-ink">
            Billing
          </span>
          <Segmented
            aria-labelledby={billingId}
            items={[
              { value: "month", label: "Monthly" },
              { value: "year", label: `Yearly (−${YEARLY_SAVING_PERCENT}%)` },
            ]}
            value={value.interval}
            onChange={(v) => set({ interval: v as BillingInterval })}
          />
        </div>
      </div>

      <Field
        label={value.plan === "enterprise" ? "Monthly price (GBP, required)" : "Negotiated monthly price (GBP, optional)"}
        hint={value.plan === "enterprise" ? "The list price before any discount." : `Leave empty to use the list price of £${plan.monthly}/mo.`}
      >
        <Input inputMode="numeric" pattern="[0-9]*" value={value.customMonthly} onChange={(e) => set({ customMonthly: e.target.value.replace(/[^0-9]/g, "") })} placeholder={plan.monthly !== null ? String(plan.monthly) : "e.g. 900"} />
      </Field>

      <fieldset className="space-y-3 rounded-lg border border-line p-3" disabled={free}>
        <legend className="px-1 text-xs font-medium text-ink-2">Discount{free && " (none on Free)"}</legend>
        <div className="flex flex-wrap items-end gap-2">
          <div className="w-28">
            <Field label="Percent off">
              <Input inputMode="numeric" pattern="[0-9]*" value={value.discountPercent} onChange={(e) => set({ discountPercent: e.target.value.replace(/[^0-9]/g, "").slice(0, 3) })} placeholder="0" />
            </Field>
          </div>
          <div className="flex flex-wrap gap-1 pb-0.5">
            <ToggleButtons
              aria-label="Quick discounts"
              items={PRESETS.map((p) => ({ value: String(p), label: p === 100 ? "100% (comp)" : `${p}%` }))}
              value={PRESETS.includes(pct) ? String(pct) : null}
              disabled={free}
              onValueChange={(v) => set({ discountPercent: v })}
            />
            {pct > 0 && (
              <Button type="button" size="sm" variant="ghost" onClick={() => set({ discountPercent: "", discountNote: "", discountEndsAt: "" })}>
                Remove
              </Button>
            )}
          </div>
        </div>
        <div className="grid gap-3 @md:grid-cols-[1fr_180px]">
          <Field label="Reason shown to the customer" hint="Appears next to their price, e.g. “Founding customer” or “Nonprofit”.">
            <Input value={value.discountNote} maxLength={80} onChange={(e) => set({ discountNote: e.target.value })} placeholder="Founding customer" disabled={!pct} />
          </Field>
          <Field label="Ends on" hint="Empty = no end date.">
            <Input type="date" value={value.discountEndsAt} onChange={(e) => set({ discountEndsAt: e.target.value })} disabled={!pct} />
          </Field>
        </div>
      </fieldset>

      <output className="block rounded-lg bg-surface-2 px-3 py-2 text-sm text-ink-2">
        {!price || !list ? (
          "Set a monthly price to see what they'll pay."
        ) : price.chargeCents === 0 ? (
          <>
            <strong className="text-ink">No charge</strong>
            {list.listCents > 0 && <> · list {formatMoney(list.listCents)}/{per}, fully discounted</>}
          </>
        ) : (
          <>
            <strong className="text-ink">
              {formatMoney(price.chargeCents)}/{per}
            </strong>
            {value.interval === "year" && <> ({formatMoney(price.perMonthCents)}/mo)</>}
            {price.savedCents > 0 && (
              <>
                {" "}
                · list {formatMoney(list.listCents)}, saves {formatMoney(price.savedCents)} a {per}
              </>
            )}
          </>
        )}
      </output>
    </div>
  );
}

const METRIC: Record<LimitUse["metric"], string> = { events: "Events, last 30 days", apps: "Apps" };

/** Usage against each plan limit. Events turn amber from 80%; anything over is red. Warns only; nothing is blocked. */
export function LimitMeters({ uses }: { uses: LimitUse[] }) {
  if (!uses.length) return <p className="text-xs text-ink-3">No fixed limits on this plan.</p>;
  return (
    <ul className="grid gap-3 @md:grid-cols-2">
      {uses.map((u) => {
        const pct = Math.round(u.ratio * 100);
        const tone = u.ratio > 1 ? "bad" : u.metric === "events" && u.ratio >= 0.8 ? "warn" : "accent";
        return (
          <li key={u.metric}>
            <div className="flex items-baseline justify-between gap-2 text-xs">
              <span className="text-ink-2">{METRIC[u.metric]}</span>
              <span className={cx("tabular", u.ratio > 1 ? "font-semibold text-bad" : "text-ink")}>
                {fmtNumber(u.used, { exact: true })} of {fmtNumber(u.limit, { exact: true })} ({pct}%){u.ratio > 1 && " · over"}
              </span>
            </div>
            <Meter
              value={u.used}
              max={u.limit}
              tone={tone}
              label={METRIC[u.metric]}
              valueText={`${fmtNumber(u.used, { exact: true })} of ${fmtNumber(u.limit, { exact: true })}, ${pct}%`}
              className="mt-1"
            />
          </li>
        );
      })}
    </ul>
  );
}

/** Account page: the workspace's plan, editable. */
export function PlanCard({ account }: { account: AccountDetail }) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState(() => draftFrom(account.plan));
  const [saving, setSaving] = useState(false);
  const saved = JSON.stringify(draftFrom(account.plan));
  useEffect(() => setDraft(draftFrom(account.plan)), [saved]);
  const dirty = JSON.stringify(draft) !== saved;
  const p = account.plan;

  const { confirm, dialog } = useConfirm();
  const [refunding, setRefunding] = useState(false);
  const stripeBilled = p.source === "stripe" && p.status !== "canceled";

  const refund = async () => {
    const ok = await confirm({
      title: "Refund the latest payment and end the plan?",
      description: "Stripe refunds the last charge in full and cancels the subscription now. The workspace moves to Free and its payers get a refund email. Our policy covers a first payment within 14 days.",
      confirmLabel: "Refund and cancel",
    });
    if (!ok) return;
    setRefunding(true);
    try {
      const r = await api<{ refundedCents: number; currency: string }>(`/admin/accounts/${account.id}/refund`, { method: "POST" });
      toaster.success({ title: `Refunded ${formatMoney(r.refundedCents, r.currency)}`, description: "The plan updates when Stripe confirms, usually within seconds." });
      await invalidateAdmin(qc);
    } catch (err) {
      notifyError(err);
    } finally {
      setRefunding(false);
    }
  };

  const save = async (e: FormEvent) => {
    e.preventDefault();
    const parsed = parseDraft(draft);
    if (!parsed.ok) return notifyError(new Error(parsed.error));
    if (
      stripeBilled &&
      !(await confirm({
        title: "This workspace pays through Stripe",
        description: "Saving here doesn't change what Stripe charges, and the next Stripe update replaces a catalog plan set here (Enterprise and custom prices stay). To change what they pay, change the subscription in Stripe too.",
        confirmLabel: "Set plan by hand",
      }))
    )
      return;
    setSaving(true);
    try {
      await api(`/admin/accounts/${account.id}/plan`, { method: "PUT", json: parsed.json });
      await invalidateAdmin(qc);
      toaster.success({ title: "Plan saved", description: "The workspace sees its new plan and price right away." });
    } catch (err) {
      notifyError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader
        title={
          <span className="flex flex-wrap items-center gap-2">
            Plan <Badge tone="accent">{PLAN_BY_ID[p.plan].name}</Badge>
            {p.discountPercent > 0 && !p.discountExpired && <Badge tone="good">{p.discountPercent}% off</Badge>}
            {p.discountExpired && <Badge tone="bad">Discount ended</Badge>}
            <Badge>{p.source === "stripe" ? "Stripe" : p.source === "trial" ? "Trial" : p.source === "early_access" ? "Early access" : "Set by hand"}</Badge>
            {p.founding && <Badge tone="good">Founding</Badge>}
            {p.status === "past_due" && <Badge tone="bad">Past due</Badge>}
            {p.cancelAtPeriodEnd && <Badge tone="bad">Cancelling</Badge>}
          </span>
        }
        subtitle={
          p.updatedAt ? (
            <>
              Last changed <Ago t={p.updatedAt} />. Owners and members see their plan, price and discount reason in Workspace settings.
            </>
          ) : (
            "On Free by default. Owners and members see their plan, price and discount reason in Workspace settings."
          )
        }
      />
      {(account.billing || p.trialEndsAt) && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-line px-4 py-3 text-sm text-ink-2">
          {p.trialDaysLeft !== null && <span>Growth trial: {p.trialDaysLeft} days left</span>}
          {p.trialEndsAt && p.trialDaysLeft === null && p.source === "trial" && <span>Trial ended <Ago t={p.trialEndsAt} /></span>}
          {p.pastDueSince && <span className="text-bad">Payment failing since <Ago t={p.pastDueSince} /></span>}
          {p.currentPeriodEnd && stripeBilled && <span>{p.cancelAtPeriodEnd ? "Ends" : "Renews"} {new Date(p.currentPeriodEnd).toLocaleDateString()}</span>}
          {account.billing?.email && <span>Receipts to {account.billing.email}</span>}
          {account.billing && (
            <span className="ml-auto flex gap-2">
              <ButtonLink to={account.billing.dashboardUrl} target="_blank" rel="noreferrer" size="sm">
                Open in Stripe<span className="sr-only"> (opens in a new tab)</span>
              </ButtonLink>
              <Button size="sm" variant="danger" onClick={refund} disabled={refunding}>
                {refunding ? "Refunding…" : "Refund and cancel"}
              </Button>
            </span>
          )}
        </div>
      )}
      <div className="border-t border-line px-4 py-3">
        <LimitMeters uses={account.limits.uses} />
        {account.limits.level !== "ok" && (
          <p className="mt-2 text-xs text-ink-2">
            Nothing is blocked: their data keeps arriving. Get in touch before anything changes; the “Plan check-in” template is written for this.
          </p>
        )}
      </div>
      <form onSubmit={save} className="border-t border-line px-4 py-3">
        {p.discountExpired && (
          <Callout tone="warn" className="mb-3">
            The {p.discountPercent}% discount ended <Ago t={p.discountEndsAt} />. They pay the list price until you extend or remove it.
          </Callout>
        )}
        <PlanFields value={draft} onChange={setDraft} />
        <div className="mt-3 flex justify-end gap-2">
          {dirty && (
            <Button type="button" variant="ghost" onClick={() => setDraft(draftFrom(p))}>
              Reset
            </Button>
          )}
          <Button variant="primary" disabled={!dirty || saving}>
            {saving ? "Saving…" : "Save plan"}
          </Button>
        </div>
      </form>
      {dialog}
    </Card>
  );
}

const REGION_REFUSED: Record<string, string> = {
  eu_needs_pro: "EU data location comes with Pro and up. Move them to Pro or above first, or tick the override.",
  workspace_has_data: "Data has arrived since this page loaded, so the location is now fixed.",
  region_unavailable: "This deployment has no EU storage yet. Provision EVENTS_DB_EU and MEDIA_EU first.",
};

/**
 * Account page: where the workspace's events, replays and media are stored. Settable only before any
 * data arrives; the control plane (accounts, settings) stays global either way.
 */
export function RegionCard({ account }: { account: AccountDetail }) {
  const qc = useQueryClient();
  const r = account.region;
  const [value, setValue] = useState<Region>(r.region);
  const [override, setOverride] = useState(false);
  const [saving, setSaving] = useState(false);
  const { confirm, dialog } = useConfirm();
  useEffect(() => setValue(r.region), [r.region]);
  const euIncluded = PLAN_BY_ID[account.plan.plan].limits.euRegion;
  const needsOverride = value === "eu" && !euIncluded;
  const blocked = value === "eu" && !r.available.includes("eu");

  const save = async (e: FormEvent) => {
    e.preventDefault();
    const ok = await confirm({
      title: value === "eu" ? "Store this workspace's data in the EU?" : "Use standard storage for this workspace?",
      description: "Events, replays, screenshots and heatmap snapshots will be stored there. Once the first data arrives, the location can't be changed here; moving it later is a manual migration.",
      confirmLabel: "Set data location",
    });
    if (!ok) return;
    setSaving(true);
    try {
      await api(`/admin/accounts/${account.id}/region`, { method: "PUT", json: { region: value, override: needsOverride && override } });
      await invalidateAdmin(qc);
      toaster.success({ title: "Data location set", description: `${REGION_LABEL[value]}. Data goes there from now on.` });
    } catch (err) {
      notifyError(err instanceof ApiError && REGION_REFUSED[err.message] ? new Error(REGION_REFUSED[err.message]) : err);
      await invalidateAdmin(qc);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader
        title={
          <span className="flex flex-wrap items-center gap-2">
            Data location <Badge tone={r.region === "eu" ? "accent" : "neutral"}>{r.region === "eu" ? "EU" : "Standard"}</Badge>
            {r.locked && <Badge>Fixed</Badge>}
          </span>
        }
        subtitle="Where events, replays, screenshots and heatmap snapshots are stored. Accounts and settings stay in the global control plane. Owners see this in Workspace settings."
      />
      {r.locked ? (
        <p className="border-t border-line px-4 py-3 text-sm text-ink-2">
          Stored in: <strong className="text-ink">{REGION_LABEL[r.region]}</strong>. This workspace already has data there, so the location can't change here. Moving it is a manual migration: see “Data residency” in docs/ARCHITECTURE.md.
        </p>
      ) : (
        <form onSubmit={save} className="space-y-3 border-t border-line px-4 py-3">
          <RadioCards
            label="Store data in"
            value={value}
            onValueChange={(v) => setValue(v as Region)}
            items={REGIONS.map((id) => ({
              value: id,
              label: REGION_LABEL[id],
              hint:
                id === "eu"
                  ? r.available.includes("eu")
                    ? "Cloudflare D1 and R2 with the EU jurisdiction. Pro plan and up."
                    : "Not available yet: this deployment has no EU storage provisioned."
                  : "Cloudflare D1 and R2 with no jurisdiction: Cloudflare decides where the data sits. The default.",
            }))}
          />
          {needsOverride && (
            <Checkbox checked={override} onCheckedChange={setOverride} hint="EU data location is sold with Pro and up. Tick to allow it on this plan anyway, e.g. for a pilot.">
              Allow EU on the {PLAN_BY_ID[account.plan.plan].name} plan
            </Checkbox>
          )}
          <p className="text-xs text-ink-2">Only possible while the workspace has no data. Set it before they install the SDK.</p>
          <div className="flex justify-end gap-2">
            <Button variant="primary" disabled={value === r.region || saving || blocked || (needsOverride && !override)}>
              {saving ? "Saving…" : "Set data location"}
            </Button>
          </div>
        </form>
      )}
      {dialog}
    </Card>
  );
}

/** Onboarding copies the new invite link as the modal closes, so it reports by toast rather than a CopyButton. */
async function copy(text: string, title: string) {
  try {
    await navigator.clipboard.writeText(text);
    toaster.success({ title });
  } catch {
    toaster.error({ title: "Couldn't copy", description: text });
  }
}

/** Account page: pending invitations, such as the owner invite from onboarding. */
export function PendingInvites({ invitations }: { invitations: AccountDetail["invitations"] }) {
  if (!invitations.length) return null;
  return (
    <Card>
      <CardHeader title="Pending invitations" subtitle="Send the link to the person invited. It only works when they sign in with that email." />
      <CardList>
        {invitations.map((i) => (
          <CardRow key={i.id} wrap>
            <div className="min-w-0 text-sm">
              <span className="text-ink">{i.email}</span> <Badge>{roleLabel(i.role)}</Badge>
              <div className={cx("text-xs", i.expiresAt < Date.now() ? "text-bad" : "text-ink-3")}>
                {i.expiresAt < Date.now() ? "Expired" : "Expires"} {new Date(i.expiresAt).toLocaleDateString()}
              </div>
            </div>
            <CopyButton text={i.url} label="Copy link" done="Invite link copied" />
          </CardRow>
        ))}
      </CardList>
    </Card>
  );
}

/** Accounts list: create a workspace for a customer, set its plan, and invite its owner. */
export function OnboardModal({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [owner, setOwner] = useState("");
  const [draft, setDraft] = useState<PlanDraft>(() => draftFrom());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName("");
    setEmail("");
    setOwner("");
    setDraft(draftFrom());
  }, [open]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const parsed = parseDraft(draft);
    if (!parsed.ok) return notifyError(new Error(parsed.error));
    setBusy(true);
    try {
      const res = await api<{ id: string; inviteUrl: string }>("/admin/accounts", {
        method: "POST",
        json: { name, ownerEmail: email, ownerName: owner || null, plan: parsed.json },
      });
      await invalidateAdmin(qc);
      onOpenChange(false);
      await copy(res.inviteUrl, "Workspace created · invite link copied");
      navigate(`/admin/accounts/${res.id}`);
    } catch (err) {
      notifyError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      wide
      title="Onboard a customer"
      description="Creates their workspace on the plan you choose and invites the owner. You aren't added to it; use support view if you need to look inside."
    >
      <form onSubmit={submit} className="@container space-y-3">
        <Field label="Workspace name">
          <Input required value={name} maxLength={80} onChange={(e) => setName(e.target.value)} placeholder="Northwind" />
        </Field>
        <div className="grid gap-3 @md:grid-cols-2">
          <Field label="Owner email" hint="They must sign in with this email to accept.">
            <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@company.com" />
          </Field>
          <Field label="Owner name (optional)">
            <Input value={owner} maxLength={120} onChange={(e) => setOwner(e.target.value)} autoComplete="off" />
          </Field>
        </div>
        <PlanFields value={draft} onChange={setDraft} />
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="primary" disabled={busy}>
            {busy ? "Creating…" : "Create workspace and invite"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
